import { ACTION_ICONS, ROLE_VISIBILITY, isVisibleForRole } from "../../config/siteConfig";
import React, { useContext, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import DatePicker from 'react-multi-date-picker';
import { CSVLink } from 'react-csv';
import toast from 'react-hot-toast';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import ContentLoader from '../loader/ContentLoader';
import { useLoadingBar } from 'react-top-loading-bar';
import { formatSupplierInfo } from '../../helper/commonHelper';
import DContext from '../../context/DContext';
import { inventoriesService } from '../../services/inventories.service';
import { airlinesService } from '../../services/airlines.service';
import { airportsService } from '../../services/airports.service';
import { suppliersService } from '../../services/suppliers.service';
import { SOMETHING_WENT_WRONG } from '../../constants/strings';
import { formatDateToDisplay } from "../../utils/dateUtils";

const STATUS_FILTER_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const selectStyles = {
  control: (base, state) => ({
    ...base, borderRadius: 8, border: "1px solid #e8edf5", background: "#f8f9fe", minHeight: 34, height: 34,
    fontSize: 13, boxShadow: "none", "&:hover": { borderColor: state.isFocused ? "var(--btn-bg)" : "#e8edf5" }
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  indicatorsContainer: (base) => ({ ...base, height: 34 }),
  placeholder: (base) => ({ ...base, color: "#a0a8b8", fontSize: 13 }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
};

const toArray = (resData) => (Array.isArray(resData) ? resData : resData?.data || []);

const initialFilters = {
  dep_airport_code: "", arr_airport_code: "", departure_date: "", arrival_date: "",
  supplier_code: "", pnr: "", airline_code: "", flight_number: "", status: ""
};

// Every supplier's inventory (no supplier_code filter). Read-only: the Action column only has a view
// button, which opens the full inventory details page.
const AllListInventory = () => {
  const { start, complete } = useLoadingBar({ color: "blue", height: 2 });
  const navigate = useNavigate();
  const { authState } = useContext(DContext);
  // same permission as /inventory/list: only these roles can switch an inventory on / off
  const showEditDelete = isVisibleForRole(ROLE_VISIBILITY.inventoryListPage.rowEditDeleteActions, authState);
  const columnCount = 12;

  const [loading, setLoading] = useState(false);
  const [inventoryRes, setInventoryRes] = useState({
    pagination: {},
    inventories: []
  });

  const [pageValue, setPageValue] = useState(1);
  const [pagingLimit, setPagingLimit] = useState("10");
  const [goPage, setGoPage] = useState("");

  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [airlineOptions, setAirlineOptions] = useState([]);
  // selected supplier option, kept so the dropdown can show "name (code)" after selection
  const [supplierOption, setSupplierOption] = useState(null);
  // inventory whose fare slabs are shown in the View Fare popup
  const [fareInventory, setFareInventory] = useState(null);

  useEffect(() => {
    loadInventory();
  }, [pagingLimit, pageValue, appliedFilters])

  useEffect(() => {
    loadAirlines();
  }, [])

  const showSwal = (title, text, icon) => {
    withReactContent(Swal).fire({ title, text, icon })
  }

  // On / off switch in the Status column (same as /inventory/list)
  const handleInventoryStatusChange = async (inventory, checked) => {
    if (!showEditDelete) return;
    const newStatus = checked ? "active" : "inactive";
    const result = await withReactContent(Swal).fire({
      title: "Update status?",
      text: `Are you sure you want to mark inventory #${inventory?.inventory_id} (PNR ${inventory?.pnr || "-"}) as ${newStatus}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, update",
      cancelButtonText: "Cancel"
    });
    if (!result.isConfirmed) return;
    try {
      setLoading(true);
      const pnrId = inventory?.fare_slabs?.[0]?.inventory_pnr_id;
      const res = await inventoriesService.updateInventoryDetail({
        inventory_pnr_id: Number(pnrId),
        status: newStatus
      });
      if (res?.success) {
        toast.success(res.message);
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    } finally {
      setLoading(false);
      loadInventory();
    }
  }

  const loadInventory = async () => {
    try {
      setLoading(true);
      start();
      const res = await inventoriesService.list({
        page: Number(pageValue),
        limit: Number(pagingLimit),
        ...appliedFilters,
      });

      if (res?.success) {
        setInventoryRes({
          pagination: res.resData?.pagination || {},
          inventories: res.resData?.data || []
        })
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    } finally {
      setLoading(false);
      complete();
    }
  }

  // Airline dropdown: airlines > list (status = active); value is the airline code the inventory API filters on
  const loadAirlines = async () => {
    try {
      const res = await airlinesService.list({ page: 1, status: "active" });
      if (res?.success) {
        setAirlineOptions(toArray(res.resData).map(a => ({ value: a.airline_code, label: `${a.airline_name} (${a.airline_code})` })));
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    }
  }

  // Departure / Arrival airport dropdowns: airports > list (status = active), searched on the server
  const loadAirports = async (search = "") => {
    try {
      const res = await airportsService.list({ page: 1, limit: 50, status: "active", ...(search ? { search } : {}) });
      if (res?.success) {
        return toArray(res.resData).map(a => ({
          value: a.airport_code,
          label: `${a.airport_name} (${a.airport_code}), ${a.city_name}`
        }));
      }
      toast.error(res?.message);
      return [];
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
      return [];
    }
  }

  // Supplier dropdown: suppliers > list (status = active), searched on the server; value is the agency name the inventory API filters on
  const loadSuppliers = async (search = "") => {
    try {
      const res = await suppliersService.list({ page: 1, status: "active", ...(search ? { search } : {}) });
      if (res?.success) {
        return (res.resData?.data || []).map(s => ({ value: s.supplier_code, label: `${s.agency_name} (${s.supplier_code})` }));
      }
      toast.error(res?.message);
      return [];
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
      return [];
    }
  }

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  }

  const handleApply = (e) => {
    e?.preventDefault();
    setPageValue(1);
    setAppliedFilters({ ...filters });
  }

  const handleReset = () => {
    setFilters(initialFilters);
    setSupplierOption(null);
    setPageValue(1);
    setAppliedFilters(initialFilters);
  }

  const handlePagingLimit = (e) => {
    setPageValue(1);
    setPagingLimit(e.target.value);
  }

  const handlePagination = (pageValueInput) => {
    setPageValue(pageValueInput);
  }

  const handleGoPage = () => {
    const n = Number(goPage);
    if (n >= 1 && n <= totalPage) {
      setPageValue(n);
    }
    setGoPage("");
  }

  const inventories = inventoryRes?.inventories || [];
  const totalRecords = inventoryRes?.pagination?.total || 0;
  const totalPage = Math.max(1, inventoryRes?.pagination?.totalPages || 1);
  const fromRecord = totalRecords === 0 ? 0 : (pageValue - 1) * Number(pagingLimit) + 1;
  const toRecord = Math.min(pageValue * Number(pagingLimit), totalRecords);

  const getPageNumbers = () => {
    if (totalPage <= 5) return Array.from({ length: totalPage }, (_, i) => i + 1);
    const sorted = [...new Set([1, 2, totalPage - 1, totalPage, pageValue - 1, pageValue, pageValue + 1])]
      .filter(n => n >= 1 && n <= totalPage).sort((a, b) => a - b);
    const out = [];
    sorted.forEach((n, i) => {
      if (i > 0 && n - sorted[i - 1] > 1) out.push("...");
      out.push(n);
    });
    return out;
  }

  const pageBtn = (active, disabled) => ({
    minWidth: 32, height: 32, padding: "0 8px", borderRadius: 8,
    border: active ? "1px solid var(--page-active-bg)" : "1px solid #e8edf5",
    background: active ? "var(--page-active-bg)" : "#fff",
    color: active ? "var(--page-active-text)" : disabled ? "#c3cad8" : "#444",
    fontSize: 13, fontWeight: 600, cursor: disabled ? "default" : "pointer"
  });

  const csvHeaders = [
    { label: "Inventory ID", key: "inventory_id" },
    { label: "Supplier", key: "supplier_info" },
    { label: "Departure", key: "dep_airport_code" },
    { label: "Arrival", key: "arr_airport_code" },
    { label: "Departure Date", key: "dep_date" },
    { label: "Departure Time", key: "dep_time" },
    { label: "Arrival Time", key: "arr_time" },
    { label: "Airline", key: "airline_name" },
    { label: "Flight No", key: "flight_number" },
    { label: "PNR", key: "pnr" },
    { label: "Seats", key: "seat_quantity" },
  ];

  // Row highlight: no PNR = orange, inactive = light red
  const rowBackground = (inventory) => {
    if (!inventory?.pnr) return "#ffe8cc";
    if (inventory?.inventory_pnr_status === "inactive") return "#fdecec";
    return "";
  }

  const filterInput = { borderRadius: 8, border: "1px solid #e8edf5", background: "#f8f9fe", fontSize: 13, height: 34 };

  return (
    <>
      <section className="section dashboard">

        {/* ── Page header ── */}
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <div>
            <h4 className="m-0 fw-bold" style={{ color: "var(--page-heading)", fontSize: 20 }}>All Inventory</h4>
            <nav className="d-flex align-items-center gap-1 mt-1" style={{ fontSize: 12, color: "#475569" }}>
              <Link to="/dashboard" style={{ color: "#475569" }}><i className="bi bi-house-door"></i></Link>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <span>Inventory</span>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <span className="fw-semibold" style={{ color: "#4a5568" }}>All Inventory List</span>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2">
            <CSVLink
              data={inventories.map(i => ({ ...i, supplier_info: formatSupplierInfo(i.supplier_info) }))}
              headers={csvHeaders}
              filename="all-inventory.csv"
              className="btn btn-sm d-flex align-items-center gap-1 px-3"
              style={{ background: "#fff", border: "1px solid #e8edf5", borderRadius: 8, height: 34, fontSize: 13, color: "#444" }}
            >
              <i className="bi bi-download"></i> Export
            </CSVLink>
          </div>
        </div>

        {/* ── Filter Card ── */}
        <form onSubmit={handleApply} className="card border-0 shadow-sm mb-3" style={{ borderRadius: 14 }}>
          <div className="card-body p-3">
            <div className="row g-2 align-items-center">
              <div className="col-12 col-md-3">
                <AsyncSelect
                  cacheOptions
                  defaultOptions
                  loadOptions={loadAirports}
                  onChange={(o) => setFilters({ ...filters, dep_airport_code: o ? o.value : "" })}
                  placeholder="Departure Airport"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
              </div>
              <div className="col-12 col-md-3">
                <AsyncSelect
                  cacheOptions
                  defaultOptions
                  loadOptions={loadAirports}
                  onChange={(o) => setFilters({ ...filters, arr_airport_code: o ? o.value : "" })}
                  placeholder="Arrival Airport"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
              </div>
              <div className="col-6 col-md-3">
                <DatePicker
                  onOpenPickNewDate={false}
                  value={filters.departure_date || null}
                  onChange={(d) => setFilters({ ...filters, departure_date: d ? d.format("YYYY-MM-DD") : "" })}
                  format="YYYY-MM-DD"
                  placeholder="Departure date"
                  editable={false}
                  inputClass="form-control"
                  containerStyle={{ width: "100%" }}
                  style={{ ...filterInput, width: "100%" }}
                />
              </div>
              <div className="col-6 col-md-3">
                <DatePicker
                  onOpenPickNewDate={false}
                  value={filters.arrival_date || null}
                  onChange={(d) => setFilters({ ...filters, arrival_date: d ? d.format("YYYY-MM-DD") : "" })}
                  format="YYYY-MM-DD"
                  placeholder="Arrival date"
                  editable={false}
                  inputClass="form-control"
                  containerStyle={{ width: "100%" }}
                  style={{ ...filterInput, width: "100%" }}
                />
              </div>
              <div className="col-6 col-md-2">
                <AsyncSelect
                  cacheOptions
                  defaultOptions
                  loadOptions={loadSuppliers}
                  value={filters.supplier_code ? (supplierOption || { value: filters.supplier_code, label: filters.supplier_code }) : null}
                  onChange={(o) => { setSupplierOption(o || null); setFilters({ ...filters, supplier_code: o ? o.value : "" }); }}
                  placeholder="Select Supplier"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
              </div>
              <div className="col-6 col-md-2">
                <input type="text" className="form-control" name="pnr" placeholder="PNR" value={filters.pnr} onChange={handleFilterChange} style={filterInput} />
              </div>
              <div className="col-6 col-md-2">
                <Select
                  options={airlineOptions}
                  value={airlineOptions.find(o => o.value === filters.airline_code) || null}
                  onChange={(o) => setFilters({ ...filters, airline_code: o ? o.value : "" })}
                  placeholder="Select Airline"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
              </div>
              <div className="col-6 col-md-2">
                <input type="text" className="form-control" name="flight_number" placeholder="Flight No." value={filters.flight_number} onChange={handleFilterChange} style={filterInput} />
              </div>
              <div className="col-6 col-md-2">
                <Select
                  options={STATUS_FILTER_OPTIONS}
                  value={STATUS_FILTER_OPTIONS.find(o => o.value === filters.status) || null}
                  onChange={(o) => setFilters({ ...filters, status: o ? o.value : "" })}
                  placeholder="Select Status"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
              </div>
              <div className="col-6 col-md-2 d-flex gap-2">
                <button type="submit" className="btn btn-sm d-flex align-items-center gap-1 text-white px-3" style={{ background: "var(--btn-bg)", borderRadius: 8, height: 34, fontSize: 13, fontWeight: 600 }}>
                  <i className="bi bi-search"></i> Apply
                </button>
                <button type="button" onClick={handleReset} className="btn btn-sm d-flex align-items-center gap-1 px-3" style={{ background: "#fff", border: "1px solid #e8edf5", borderRadius: 8, height: 34, fontSize: 13, color: "#444" }}>
                  <i className="bi bi-arrow-counterclockwise"></i> Reset
                </button>
              </div>
            </div>
          </div>
        </form>

        {/* ── Table Card ── */}
      <div className="card border-0 shadow-sm" style={{ borderRadius: 14, overflow: "hidden" }}>
          <div>

                {/* Card Header */}
                {/* <div className="d-flex align-items-center justify-content-between mb-3">
                  <div className="d-flex align-items-center gap-2">
                    <div style={{
                      width: "34px", height: "34px", borderRadius: "9px",
                      background: "linear-gradient(135deg, var(--btn-hover-bg), var(--btn-bg))",
                      display: "flex", alignItems: "center", justifyContent: "center"
                    }}>
                      <i className="bi bi-airplane-fill text-white" style={{ fontSize: "14px" }}></i>
                    </div>
                    <div>
                      <h6 className="mb-0 fw-bold" style={{ color: "var(--btn-hover-bg)", fontSize: "14px" }}>Inventory List</h6>
                      <span style={{ fontSize: "11px", color: "#475569" }}>
                        Total: <strong style={{ color: "var(--btn-bg)" }}>{inventoryRes?.pagination?.total ?? 0}</strong> records
                      </span>
                    </div>
                  </div>
                  <Link to="/inventory/add" style={{ textDecoration: "none" }}>
                    <button className="btn btn-sm fw-semibold d-flex align-items-center gap-2 text-white"
                      style={{
                        background: "linear-gradient(135deg, var(--btn-hover-bg), var(--btn-bg))",
                        borderRadius: "8px", border: "none",
                        boxShadow: "0 4px 12px rgba(14,106,150,0.25)",
                        fontSize: "13px", height: "34px", padding: "0 16px"
                      }}>
                      <i className="bi bi-plus-circle-fill"></i> Add New PNR
                    </button>
                  </Link>
                </div> */}

                {/* Loading Overlay */}
                {loading && (
                  <div style={{
                    position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
                    background: "rgba(255,255,255,0.7)",
                    display: "flex", justifyContent: "center", alignItems: "center",
                    zIndex: 10, borderRadius: "14px"
                  }}>
                    <ContentLoader loaderType="circleSpin" />
                  </div>
                )}

                {/* Table */}
                <div className="table-responsive">
                  <table className="table table-hover mb-0" style={{ fontSize: "13px" }}>
                    <thead>
                      <tr style={{ background: "#f4f6fb" }}>
                        {["S.No.", "Supplier", "Departure", "Arrival", "Dept – Arr Time", "Airline (Flight No)", "PNR", "Dept. Date", "Seats", "Fare", "Status", "Action"].map((h, i) => (
                          <th key={i} style={{
                            fontSize: "11px", fontWeight: "700", color: "#475569",
                            textTransform: "uppercase", letterSpacing: "0.6px",
                            border: "none", padding: "10px 12px", whiteSpace: "nowrap",
                            background: "#f4f6fb"
                          }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {inventories.length > 0 ? (
                        inventories.map((inventory, idx) => (
                          <tr
                            key={`${inventory?.inventory_id}-${inventory?.pnr}-${idx}`}
                            style={{ borderBottom: "1px solid #f0f2f8", verticalAlign: "middle", background: rowBackground(inventory) }}
                            onMouseEnter={(e) => { if (!rowBackground(inventory)) e.currentTarget.style.background = "#f8f9fe"; }}
                            onMouseLeave={(e) => (e.currentTarget.style.background = rowBackground(inventory))}
                          >
<td className="ps-3" style={{ fontSize: 13 }}>{(pageValue - 1) * Number(pagingLimit) + idx + 1}</td>
                            {/* Supplier */}
                            <td style={{ padding: "10px 12px" }}>
                              <div className="d-flex align-items-start gap-2">
                                {/* <div
                                  style={{
                                    width: "26px",
                                    height: "26px",
                                    borderRadius: "7px",
                                    background: "#e6f1f8",
                                    flexShrink: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center"
                                  }}
                                >
                                  <i className="bi bi-building" style={{ fontSize: "11px", color: "var(--btn-bg)" }}></i>
                                </div> */}
                                <div>
                                  <div style={{ fontWeight: "700", color: "var(--btn-hover-bg)", fontSize: "12px" }}>
                                    {inventory?.supplier_name}
                                  </div>
                                  <div style={{ fontSize: "11px", color: "#6b7280" }}>
                                    {inventory?.supplier_code}
                                  </div>
                                </div>
                              </div>
                            </td>

                            {/* Departure */}
                            <td style={{ padding: "10px 12px" }}>
                              <div style={{ fontWeight: "600", color: "var(--btn-hover-bg)", fontSize: "12px" }}>
                                {inventory?.dep_city_name || "-"}
                              </div>
                              <div style={{ fontSize: "10px", color: "#6b7280" }}>
                                  {inventory?.dep_airport_code || "-"}
                                  </div>
                              {/* <span
                                style={{
                                  marginLeft: "4px",
                                  fontSize: "10px",
                                  fontWeight: "700",
                                  background: "#e6f1f8",
                                  color: "var(--btn-bg)",
                                  borderRadius: "5px",
                                  padding: "1px 6px"
                                }}
                              >
                                {inventory?.dep_airport_code || "-"}
                              </span> */}
                            </td>

                            {/* Arrival */}
                            <td style={{ padding: "10px 12px" }}>
                              <div style={{ fontWeight: "600", color: "var(--btn-hover-bg)", fontSize: "12px" }}>
                                {inventory?.arr_city_name || "-"}
                              </div>
                               <div style={{ fontSize: "10px", color: "#6b7280" }}>
                                  {inventory?.arr_airport_code || "-"}
                                  </div>
                              {/* <span
                                style={{
                                  marginLeft: "4px",
                                  fontSize: "10px",
                                  fontWeight: "700",
                                  background: "#e6f1f8",
                                  color: "var(--btn-bg)",
                                  borderRadius: "5px",
                                  padding: "1px 6px"
                                }}
                              >
                                {inventory?.arr_airport_code || "-"}
                              </span> */}
                            </td>

                            {/* Dept – Arr Time */}
                            <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                              <span style={{ fontSize: "12px", color: "#374151", fontWeight: "600" }}>
                                <i className="bi bi-clock me-1" style={{ color: "#475569", fontSize: "10px" }}></i>
                                {inventory?.dep_time || "-"}
                              </span>
                              <span style={{ margin: "0 4px", color: "#475569" }}>→</span>
                              <span style={{ fontSize: "12px", color: "#374151", fontWeight: "600" }}>
                                {inventory?.arr_time || "-"}
                              </span>
                            </td>

                            {/* Airline (Flight No) */}
                            <td style={{ padding: "10px 12px" }}>
                              <div className="d-flex align-items-center gap-2">
                                <img
                                  style={{
                                    width: "22px",
                                    height: "22px",
                                    borderRadius: "5px",
                                    objectFit: "contain",
                                    background: "#f4f6fb",
                                    padding: "2px"
                                  }}
                                  src={`https://images.kiwi.com/airlines/32/${inventory?.airline_code}.png`}
                                  alt={inventory?.airline_code || "airline"}
                                />
                                <div>
                                  <div style={{ fontWeight: "700", color: "#374151", fontSize: "12px", lineHeight: 1.1 }}>
                                    {inventory?.airline_name || "-"}
                                  </div>
                                  <div style={{ fontSize: "11px", color: "#6b7280" }}>
                                    {inventory?.airline_code || "-"} - {inventory?.flight_number || "-"}
                                  </div>
                                </div>
                              </div>
                            </td>

                            {/* PNR */}
                            <td style={{ padding: "10px 12px" }}>
                              <span
                                style={{                                  
                                  color: "var(--btn-hover-bg)",
                                  fontSize: "11px",
                                  fontWeight: "700",
                                }}
                              >
                                {inventory?.pnr || "-"}
                              </span>
                            </td>

                            {/* Dept. Date */}
                            <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                              <span
                                style={{
                                  fontSize: "12px",
                                  //color: "#6b7280",
                                  fontWeight: "600",
                                  // background: "#f4f6fb",
                                  //borderRadius: "6px",
                                  //padding: "2px 8px"
                                }}
                              >
                                <i className="bi bi-calendar3 me-1" style={{ color: "#475569" }}></i>
                                {formatDateToDisplay(inventory?.dep_date) || "-"}
                              </span>
                            </td>

                            {/* Seats */}
                            <td style={{ padding: "10px 12px" }}>
                              <span
                                style={{
                                  background: "#ecfdf5",
                                  color: "#065f46",
                                  borderRadius: "6px",
                                  padding: "3px 8px",
                                  fontSize: "11px",
                                  fontWeight: "700"
                                }}
                              >
                                <i className="bi bi-person-fill me-1" style={{ fontSize: "10px" }}></i>
                                {inventory?.available_seats ?? 0}
                              </span>
                            </td>

                            {/* Fare */}
                            <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                              <button
                                type="button"
                                style={{
                                  background: "#e6f1f8",
                                  border: "none",
                                  borderRadius: "7px",
                                  width: "28px",
                                  height: "28px",
                                  padding: 0,
                                  marginRight: "4px"
                                }}
                                data-bs-toggle="modal"
                                data-bs-target="#ViewFare"
                                onClick={() => setFareInventory(inventory)}
                                title="View Fare"
                                onMouseEnter={(e) => (e.currentTarget.style.background = "#dde3ef")}
                                onMouseLeave={(e) => (e.currentTarget.style.background = "#e6f1f8")}
                              >
                                <i className={`bi ${ACTION_ICONS.view}`} data-action="view" style={{ fontSize: "11px", color: "var(--btn-bg)" }}></i>
                              </button>
                            </td>


                            {/* Active / Inactive */}
                            <td style={{ padding: "10px 12px" }}>
                              <div className="form-check form-switch m-0 d-flex align-items-center justify-content-center p-0">
                                <input
                                  type="checkbox"
                                  role="switch"
                                  className="form-check-input m-0"
                                  checked={inventory?.inventory_pnr_status !== "inactive"}
                                  onChange={(e) => handleInventoryStatusChange(inventory, e.target.checked)}
                                  disabled={!showEditDelete}
                                  title={inventory?.inventory_pnr_status === "inactive" ? "Inactive" : "Active"}
                                  style={{
                                    width: "38px",
                                    height: "20px",
                                    cursor: "pointer",
                                    backgroundColor: inventory?.inventory_pnr_status === "inactive" ? "#dc2626" : "#16a34a",
                                    borderColor: inventory?.inventory_pnr_status === "inactive" ? "#dc2626" : "#16a34a",
                                    backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%23fff'/%3e%3c/svg%3e\")",
                                    backgroundPosition: inventory?.inventory_pnr_status === "inactive" ? "left center" : "right center",
                                    opacity: 1
                                  }}
                                />
                              </div>
                            </td>

                            {/* Action */}                            <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                              <button
                                type="button"
                                className="btn btn-sm"
                                style={{ background: "#e6f1f8", color: "var(--btn-bg)", border: "none", borderRadius: "7px", width: "28px", height: "28px", padding: 0 }}
                                onClick={() => navigate("/inventory/details/" + inventory?.inventory_id, { state: { inventory, from: "/inventory/all-inventory-list" } })}
                                title="View"
                              >
                                <i className={`bi ${ACTION_ICONS.view}`} data-action="view" style={{ fontSize: "11px" }}></i>
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={columnCount} className="text-center py-5">
                            <div style={{ color: "#475569" }}>
                              <i
                                className="bi bi-inbox"
                                style={{ fontSize: "36px", display: "block", marginBottom: "8px" }}
                              ></i>
                              <span style={{ fontSize: "13px" }}>No inventory found</span>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 py-3" style={{ borderTop: "1px solid #eef1f6", fontSize: 13, color: "#6b7a90" }}>
                  <div>Showing <b>{fromRecord}–{toRecord}</b> of <b>{totalRecords}</b> inventories</div>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span>Rows</span>
                    <select className="form-select form-select-sm" style={{ width: 100, borderRadius: 8 }} value={pagingLimit} onChange={handlePagingLimit}>
                      {["10", "20", "50", "100", "500"].map(v => <option key={v} value={v}>{v}/page</option>)}
                    </select>
                    <span>Go</span>
                    <input type="number" min="1" max={totalPage} className="form-control form-control-sm text-center" style={{ width: 60, borderRadius: 8 }}
                      value={goPage} onChange={(e) => setGoPage(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleGoPage()} placeholder={String(pageValue)} />
                    <span>/ {totalPage}</span>
                    <button type="button" style={pageBtn(false, false)} onClick={handleGoPage}>Go</button>
                    <div className="d-flex align-items-center gap-1">
                      <button type="button" disabled={pageValue <= 1} style={pageBtn(false, pageValue <= 1)} onClick={() => handlePagination(1)}><i className="bi bi-chevron-double-left"></i></button>
                      <button type="button" disabled={pageValue <= 1} style={pageBtn(false, pageValue <= 1)} onClick={() => handlePagination(pageValue - 1)}><i className="bi bi-chevron-left"></i></button>
                      {getPageNumbers().map((n, i) => n === "..."
                        ? <span key={"e" + i} className="px-1">...</span>
                        : <button type="button" key={n} style={pageBtn(n === pageValue, false)} onClick={() => handlePagination(n)}>{n}</button>)}
                      <button type="button" disabled={pageValue >= totalPage} style={pageBtn(false, pageValue >= totalPage)} onClick={() => handlePagination(pageValue + 1)}><i className="bi bi-chevron-right"></i></button>
                      <button type="button" disabled={pageValue >= totalPage} style={pageBtn(false, pageValue >= totalPage)} onClick={() => handlePagination(totalPage)}><i className="bi bi-chevron-double-right"></i></button>
                    </div>
                  </div>
                </div>

              </div>
            </div>
      </section>

      {/* ── View Fare Modal ── */}
      <div className="modal fade" id="ViewFare" tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content border-0 shadow" style={{ borderRadius: "16px", overflow: "hidden" }}>
            <div style={{ height: "4px", background: "linear-gradient(135deg, var(--btn-hover-bg), var(--btn-bg))" }}></div>
            <div className="modal-header border-0">
              <div className="d-flex align-items-center gap-3">
                <div style={{ width: "38px", height: "38px", borderRadius: "10px", background: "linear-gradient(135deg, var(--btn-hover-bg), var(--btn-bg))", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <i className="bi bi-eye-fill text-white" style={{ fontSize: "15px" }}></i>
                </div>
                <div>
                  <h6 className="mb-0 fw-bold" style={{ color: "#fff", fontSize: "15px" }}>Fare Details</h6>
                  <p className="mb-0" style={{ fontSize: "11px", color: "#fff" }}>View current fare slabs</p>
                </div>
              </div>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body px-4 pb-4">
              {fareInventory && (
                <div className="row g-3 mb-3">
                  {[
                    { label: "Supplier", value: fareInventory.supplier_name || formatSupplierInfo(fareInventory.supplier_info) },
                    { label: "Departure", value: `${fareInventory.dep_city_name || "-"} (${fareInventory.dep_airport_code || "-"})` },
                    { label: "Arrival", value: `${fareInventory.arr_city_name || "-"} (${fareInventory.arr_airport_code || "-"})` },
                    { label: "Dept – Arr Time", value: `${String(fareInventory.dep_time || "-").slice(0, 5)} – ${String(fareInventory.arr_time || "-").slice(0, 5)}` },
                    { label: "Airline | Flight No", value: `${fareInventory.airline_name || fareInventory.airline_code || "-"} | ${fareInventory.airline_code || "-"} | ${fareInventory.flight_number || "-"}` },
                    { label: "Dept. Date", value: fareInventory.dep_date || "-" },
                  ].map((f) => (
                    <div className="col-6 col-md-4" key={f.label}>
                      <div style={{ fontSize: "11px", fontWeight: "700", color: "#6c757d", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "2px" }}>{f.label}</div>
                      <div style={{ fontSize: "13px", fontWeight: "600", color: "#1f2a44" }}>{f.value}</div>
                    </div>
                  ))}
                </div>
              )}
              <table className="table mb-0" style={{ fontSize: "13px" }}>
                <thead>
                  <tr style={{ background: "#f4f6fb" }}>
                    {["Fare", "Sold Seat", "Unsold Seat", "Total Seat", "Departure Date"].map((h, i) => (
                      <th key={i} style={{ fontSize: "11px", fontWeight: "700", color: "#475569", textTransform: "uppercase", letterSpacing: "0.6px", border: "none", padding: "10px 12px", background: "#f4f6fb" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(fareInventory?.fare_slabs || []).map((slab, i) => (
                    <tr key={slab?.inventory_pnr_id ?? i} style={{ borderBottom: "1px solid #f0f2f8", verticalAlign: "middle" }}>
                      <td style={{ padding: "10px 12px" }}>
                        <span style={{ background: "#ecfdf5", color: "#065f46", borderRadius: "6px", padding: "3px 8px", fontSize: "12px", fontWeight: "700" }}>₹ {Number(slab?.total_amount ?? 0).toLocaleString("en-IN")}</span>
                      </td>
                      <td style={{ padding: "10px 12px", color: "#374151", fontWeight: "600" }}>{slab?.booked_quantity ?? 0}</td>
                      <td style={{ padding: "10px 12px", color: "#374151", fontWeight: "600" }}>{slab?.available_seats ?? 0}</td>
                      <td style={{ padding: "10px 12px" }}>
                        <span style={{ background: "#e6f1f8", color: "var(--btn-bg)", borderRadius: "6px", padding: "2px 8px", fontSize: "11px", fontWeight: "700" }}>{slab?.seat_quantity ?? 0}</span>
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        <span style={{ fontSize: "11px", color: "#6b7280", fontWeight: "600", background: "#f4f6fb", borderRadius: "6px", padding: "2px 8px" }}>
                          <i className="bi bi-calendar3 me-1" style={{ color: "#475569" }}></i>{formatDateToDisplay(fareInventory?.dep_date) || "-"}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {(fareInventory?.fare_slabs || []).length === 0 && (
                    <tr>
                      <td colSpan={5} className="text-center text-muted py-4" style={{ fontSize: "13px" }}>No fare slabs found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

    </>
  )
}

export default AllListInventory;